import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { resolveWallSides } from './Wall3D.jsx';

// «عینکِ مهندسی» در حالتِ اول‌شخص. وقتی روشن است، بسته به جایی که کاربر نگاه می‌کند:
//
//   ۱) به یک دیوار (بخشِ توپر): طولِ کلِ دیوار + ارتفاعِ هر بخشِ پوششِ همان رویه (پایین/بالا یا کلِ ارتفاع)
//   ۲) به یک در/پنجره/بازشو روی دیوار: طولِ دیوار + عرض و ارتفاعِ خودِ آن + (پنجره) ارتفاعِ لبه‌ی پایین از
//      کف + فاصله‌اش تا دو گوشه‌ی دیوار
//   ۳) به کف (سر پایین): طول و عرضِ اتاقی که کاربر داخلش است + مساحت (مترمربع)
//
// همه‌ی اندازه‌ها «محورِ دیوار» (همان اندازه‌های نقشه) هستند، پس جمعِ فاصله‌ها و عرض‌ها دقیقاً طولِ دیوار
// می‌شود. اندازه‌های دیوار مالِ «همان رویه‌ای» است که کاربر روبه‌رویش ایستاده (هر دیوار دو رویه‌ی مستقل دارد).

const MAX_LOOK_DISTANCE_CM = 2000;
const TIE_EPSILON_CM = 3; // دو دیوارِ هم‌خط (دیوارِ مشترکِ دو اتاق) با این اختلاف «هم‌فاصله» حساب می‌شوند
const FACE_GAP_CM = 1.8; // فاصله‌ی خطوط از سطحِ رویه، تا روی خودِ دیوار نچسبند
const LINE_THICKNESS_CM = 1.2;
const TICK_CM = 16;
const TOP_LINE_DROP_CM = 28; // خطِ طولِ دیوار کمی پایین‌تر از سقف
const VERTICAL_EDGE_MARGIN_CM = 35; // خطِ عمودی به لبه‌های دیوار نچسبد
const GAZE_STEP_CM = 10; // جابه‌جاییِ خطِ عمودی با نگاه، در گام‌های ۱۰ سانتی (نه هر فریم، برای کارایی)
const ITEM_HIT_MARGIN_CM = 8; // نگاه کمی بیرون از لبه‌ی در/پنجره هم هنوز «روی آن» حساب شود
const ITEM_SIDE_GAP_CM = 14; // فاصله‌ی خطوطِ اندازه‌ی در/پنجره از لبه‌هایش
const CORNER_LINE_Y_CM = 22; // ارتفاعِ خطِ «فاصله تا گوشه»
const STEP_LOOK_RANGE_CM = 260; // تا این فاصله، نگاهِ رو به پایین به یک پله، اندازه‌ی پله را نشان می‌دهد
const FLOOR_PITCH = -0.62; // حدودِ ۳۵ درجه سر پایین = «نگاه به کف»
const FLOOR_LINE_Y_CM = 1.5;
const FLOOR_EDGE_INSET_CM = 45; // خطوطِ ابعادِ اتاق کمی داخل‌تر از دیوارها
// هر «نوع» اندازه یک رنگِ جدا دارد تا در یک نگاه از هم تفکیک شوند (خواسته‌ی کاربر):
const DIM_COLORS = {
  wall: '#ffc21a', // طول و ارتفاعِ دیوار / بخش‌های پوشش — زرد
  item: '#38bdf8', // عرض و ارتفاعِ خودِ در/پنجره/بازشو — آبی
  offset: '#f472b6', // فاصله‌ها: تا گوشه‌ها و ارتفاع از کف — صورتی
  room: '#4ade80', // طول و عرضِ اتاق روی کف — سبز
  step: '#fb923c', // پله / اختلافِ سطحِ کف — نارنجی
};

const faNumber = (n, digits = 0) =>
  Number(n).toLocaleString('fa-IR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const cm = (n) => `${faNumber(Math.round(n))} سانتی‌متر`;

// مادّه‌ی خطوطِ اندازه: همیشه روی همه‌چیز دیده شود (depthTest خاموش)، به نور/tone mapping و
// «روشناییِ اتاق» (roomLighting.jsx → khNoRoomLight) واکنش ندهد. userData ثابت (نه شیءِ تازه در هر رندر)
const DIM_USERDATA = { khNoRoomLight: true };
function DimMaterial({ color }) {
  return (
    <meshBasicMaterial
      color={color}
      depthTest={false}
      depthWrite={false}
      transparent
      opacity={0.95}
      toneMapped={false}
      userData={DIM_USERDATA}
    />
  );
}

function Bar({ position, size, color }) {
  return (
    <mesh position={position} renderOrder={1000}>
      <boxGeometry args={size} />
      <DimMaterial color={color} />
    </mesh>
  );
}

// ---------------------------------------------------------------------------------------------------
// جلوگیری از رویِ‌هم‌افتادنِ برچسب‌ها: هر برچسب (یک div داخلِ drei <Html>) خودش را این‌جا ثبت می‌کند و هر
// دو فریم یک بار، مستطیلِ واقعیِ همه روی صفحه اندازه گرفته می‌شود؛ هرکدام که با برچسب‌های قبلی (به ترتیبِ
// اولویت) هم‌پوشانی داشته باشد، به کمترین فاصله‌ی لازم بالا یا پایین جابه‌جا می‌شود. جابه‌جایی فقط روی
// خودِ div است (drei موقعیتِ wrapper را هر فریم می‌نویسد، پس با هم تداخل ندارند)
const LABEL_GAP_PX = 4;
const LabelLayoutContext = createContext(null);

function useLabelRegistry() {
  const listRef = useRef([]);
  const register = useCallback((el, priority) => {
    if (!el) return () => {};
    const entry = { el, priority, dy: 0 };
    listRef.current.push(entry);
    return () => {
      listRef.current = listRef.current.filter((e) => e !== entry);
    };
  }, []);
  return { listRef, register };
}

function LabelLayout({ listRef }) {
  const frameRef = useRef(0);
  useFrame(() => {
    frameRef.current += 1;
    if (frameRef.current % 2) return;
    const entries = [...listRef.current].sort((a, b) => a.priority - b.priority);
    const placed = [];
    entries.forEach((e) => {
      const r = e.el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return; // پشتِ دوربین/پنهان
      // مستطیلِ «بدونِ جابه‌جایی» (dy فعلی از آن کم می‌شود)
      const base = { left: r.left, right: r.right, top: r.top - e.dy, bottom: r.bottom - e.dy };
      const overlaps = (dy) =>
        placed.find(
          (p) =>
            base.left < p.right + LABEL_GAP_PX &&
            base.right > p.left - LABEL_GAP_PX &&
            base.top + dy < p.bottom + LABEL_GAP_PX &&
            base.bottom + dy > p.top - LABEL_GAP_PX
        );
      // کوچک‌ترین جابه‌جاییِ بی‌برخورد را بین بالا و پایین پیدا می‌کنیم
      const tryDir = (sign) => {
        let dy = 0;
        for (let i = 0; i < 12; i += 1) {
          const hit = overlaps(dy);
          if (!hit) return dy;
          dy = sign > 0 ? hit.bottom + LABEL_GAP_PX - base.top : hit.top - LABEL_GAP_PX - base.bottom;
        }
        return null;
      };
      const down = tryDir(1);
      const up = tryDir(-1);
      let dy = 0;
      if (down != null && up != null) dy = Math.abs(up) < Math.abs(down) ? up : down;
      else dy = down ?? up ?? 0;
      dy = Math.round(dy);
      if (dy !== e.dy) {
        e.dy = dy;
        e.el.style.transform = dy ? `translateY(${dy}px)` : '';
      }
      placed.push({ left: base.left, right: base.right, top: base.top + dy, bottom: base.bottom + dy });
    });
  });
  return null;
}

// priority: عددِ کوچک‌تر = مهم‌تر (سرِ جایش می‌ماند، بقیه کنار می‌روند)
function Label({ position, children, strong = false, color, priority = 5 }) {
  const registry = useContext(LabelLayoutContext);
  const cleanupRef = useRef(null);
  const setRef = useCallback(
    (el) => {
      if (cleanupRef.current) cleanupRef.current();
      cleanupRef.current = registry && el ? registry(el, priority) : null;
    },
    [registry, priority]
  );
  return (
    <Html position={position} center zIndexRange={[15, 10]} style={{ pointerEvents: 'none' }}>
      <div
        ref={setRef}
        className={`create-3d__dim-label${strong ? ' is-strong' : ''}`}
        style={color ? { '--dim-color': color } : undefined}
      >
        {children}
      </div>
    </Html>
  );
}

// خطِ اندازه در راستای یکی از محورها (axis: 'x' | 'y' | 'z')، از a تا b، با تیک در دو سر.
// at: [x, y, z]ِ یک نقطه روی خط (مؤلفه‌ی همان محور نادیده گرفته می‌شود). tickAxis: جهتِ تیک‌ها.
// labelOffset: جابه‌جاییِ برچسب نسبت به وسطِ خط
function DimLine({ axis, a, b, at, tickAxis, label, labelOffset = [0, 0, 0], color = DIM_COLORS.wall, priority = 5 }) {
  const len = Math.abs(b - a);
  if (len < 1) return null;
  const i = { x: 0, y: 1, z: 2 }[axis];
  const ti = { x: 0, y: 1, z: 2 }[tickAxis];
  const mid = [...at];
  mid[i] = (a + b) / 2;
  const lineSize = [LINE_THICKNESS_CM, LINE_THICKNESS_CM, LINE_THICKNESS_CM];
  lineSize[i] = len;
  const tickSize = [LINE_THICKNESS_CM, LINE_THICKNESS_CM, LINE_THICKNESS_CM];
  tickSize[ti] = TICK_CM;
  const p1 = [...at];
  p1[i] = a;
  const p2 = [...at];
  p2[i] = b;
  return (
    <group>
      <Bar position={mid} size={lineSize} color={color} />
      <Bar position={p1} size={tickSize} color={color} />
      <Bar position={p2} size={tickSize} color={color} />
      {label && (
        <Label
          position={[mid[0] + labelOffset[0], mid[1] + labelOffset[1], mid[2] + labelOffset[2]]}
          color={color}
          priority={priority}
        >
          {label}
        </Label>
      )}
    </group>
  );
}

// آیا دیوار یکی از چهار لبه‌ی این اتاق است (هر دو سرش روی لبه‌ی مستطیل) — فقط وقتی wallIds نباشد
function wallBelongsToRoom(wall, rect, tol = 6) {
  if (!rect) return false;
  const onEdge = (x, y) =>
    x >= Math.min(rect.x1, rect.x2) - tol &&
    x <= Math.max(rect.x1, rect.x2) + tol &&
    y >= Math.min(rect.y1, rect.y2) - tol &&
    y <= Math.max(rect.y1, rect.y2) + tol &&
    (Math.abs(x - rect.x1) <= tol || Math.abs(x - rect.x2) <= tol || Math.abs(y - rect.y1) <= tol || Math.abs(y - rect.y2) <= tol);
  return onEdge(wall.x1, wall.y1) && onEdge(wall.x2, wall.y2);
}

function findRoomAt(rooms, pos) {
  return (rooms || []).find(
    (r) =>
      pos.x >= Math.min(r.x1, r.x2) && pos.x <= Math.max(r.x1, r.x2) && pos.z >= Math.min(r.y1, r.y2) && pos.z <= Math.max(r.y1, r.y2)
  );
}

// دیوارِ روبه‌روی نگاه: پرتوِ افقی از جای کاربر در جهتِ نگاه، اولین برخورد با خطِ مرکزیِ دیوارها.
// خروجی: { wallId, side, along (پله‌ای، برای خطِ عمودی), alongExact (برای تشخیصِ در/پنجره) }
function findLookedAtWall(walls, pos, yaw, rooms) {
  const dx = -Math.sin(yaw);
  const dz = -Math.cos(yaw);
  const hits = [];
  walls.forEach((wall) => {
    const ex = wall.x2 - wall.x1;
    const ez = wall.y2 - wall.y1;
    const len = Math.hypot(ex, ez);
    if (len < 1) return;
    const denom = dx * ez - dz * ex;
    if (Math.abs(denom) < 1e-9) return; // موازی با نگاه
    const wx = wall.x1 - pos.x;
    const wz = wall.y1 - pos.z;
    const t = (wx * ez - wz * ex) / denom; // فاصله روی پرتو
    const s = (wx * dz - wz * dx) / denom; // نسبت روی دیوار (۰ تا ۱)
    if (t <= 0 || t > MAX_LOOK_DISTANCE_CM || s < 0 || s > 1) return;
    hits.push({ wall, t, s, len });
  });
  if (!hits.length) return null;
  hits.sort((a, b) => a.t - b.t);
  const nearest = hits.filter((h) => h.t - hits[0].t <= TIE_EPSILON_CM);
  // دیوارِ مشترکِ دو اتاق معمولاً دو دیوارِ روی‌هم است — دیوارِ همان اتاقی که کاربر داخلش ایستاده اولویت دارد
  const room = findRoomAt(rooms, pos);
  const hit =
    (room?.wallIds && nearest.find((h) => room.wallIds.includes(h.wall.id))) ||
    nearest.find((h) => wallBelongsToRoom(h.wall, room)) ||
    nearest[0];
  const { wall, s, len } = hit;
  const ux = (wall.x2 - wall.x1) / len;
  const uz = (wall.y2 - wall.y1) / len;
  // سمتِ «a» رو به +Z محلیِ دیوار است = بردارِ (-uz, ux) در صفحه‌ی نقشه (همان قراردادِ Wall3D در Create3D)
  const side = (pos.x - wall.x1) * -uz + (pos.z - wall.y1) * ux >= 0 ? 'a' : 'b';
  const alongExact = s * len;
  const along = Math.round(alongExact / GAZE_STEP_CM) * GAZE_STEP_CM;
  return { wallId: wall.id, side, along, alongExact, t: hit.t };
}

// در/پنجره/بازشویی که نگاه رویش افتاده (items: [{kind,id,start,end,bottom,top}] روی همین دیوار)
function findItemAt(items, alongExact) {
  if (!items) return null;
  return (
    items.find((it) => alongExact >= it.start - ITEM_HIT_MARGIN_CM && alongExact <= it.end + ITEM_HIT_MARGIN_CM) || null
  );
}

// تعیینِ هدف در هر فریم. خروجی یک شیء با key (برای این‌که فقط با تغییرِ واقعی setState شود)
// level: ارتفاعِ کفِ جایی که کاربر ایستاده (اختلافِ سطح) — ارتفاع‌ها از همین کف اندازه گرفته می‌شوند
export function computeGlassesTarget({ walls, rooms, wallItems, pos, yaw, pitch, level = 0 }) {
  const t = computeGlassesTargetRaw({ walls, rooms, wallItems, pos, yaw, pitch });
  return t ? { ...t, level, key: `${t.key}@${level}` } : null;
}

function computeGlassesTargetRaw({ walls, rooms, wallItems, pos, yaw, pitch }) {
  if (pitch < FLOOR_PITCH) {
    // سر پایین جلوی یک در/بازشوی پله‌دار (نزدیک) → اندازه‌ی پله مهم‌تر از ابعادِ اتاق است
    const near = findLookedAtWall(walls, pos, yaw, rooms);
    if (near && near.t < STEP_LOOK_RANGE_CM) {
      const it = findItemAt(wallItems?.get(near.wallId), near.alongExact);
      if (it?.levels && Math.abs((it.levels.a || 0) - (it.levels.b || 0)) >= 1) {
        return {
          kind: 'item',
          wallId: near.wallId,
          side: near.side,
          itemKey: `${it.kind}:${it.id}`,
          key: `item:${near.wallId}:${near.side}:${it.kind}:${it.id}`,
        };
      }
    }
    const room = findRoomAt(rooms, pos);
    return room ? { kind: 'floor', roomId: room.id, key: `floor:${room.id}` } : null;
  }
  const hit = findLookedAtWall(walls, pos, yaw, rooms);
  if (!hit) return null;
  const item = findItemAt(wallItems?.get(hit.wallId), hit.alongExact);
  if (item) {
    return {
      kind: 'item',
      wallId: hit.wallId,
      side: hit.side,
      itemKey: `${item.kind}:${item.id}`,
      key: `item:${hit.wallId}:${hit.side}:${item.kind}:${item.id}`,
    };
  }
  return {
    kind: 'wall',
    wallId: hit.wallId,
    side: hit.side,
    along: hit.along,
    key: `wall:${hit.wallId}:${hit.side}:${hit.along}`,
  };
}

function useWallFrame(wall) {
  const dx = wall.x2 - wall.x1;
  const dy = wall.y2 - wall.y1;
  const length = Math.hypot(dx, dy);
  const quaternion = useMemo(() => {
    const dir = length > 0 ? new THREE.Vector3(dx, 0, dy).normalize() : new THREE.Vector3(1, 0, 0);
    return new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(1, 0, 0), dir);
  }, [dx, dy, length]);
  return { length, quaternion };
}

// ۱) دیوار: طول + ارتفاعِ بخش‌های پوشش
function WallDimensions({ wall, side, along, wallThickness, wallHeightCm, level = 0 }) {
  const { length, quaternion } = useWallFrame(wall);
  if (length <= 0) return null;
  const z = (side === 'a' ? 1 : -1) * (wallThickness / 2 + FACE_GAP_CM);
  const H = wallHeightCm;
  const lower = resolveWallSides(wall)?.[side]?.lower;
  const split = lower ? Math.min(H, Math.max(level, level + (lower.heightCm || 0))) : null;
  const vx =
    length > VERTICAL_EDGE_MARGIN_CM * 2
      ? Math.min(length - VERTICAL_EDGE_MARGIN_CM, Math.max(VERTICAL_EDGE_MARGIN_CM, along))
      : length / 2;

  return (
    <group position={[wall.x1, 0, wall.y1]} quaternion={quaternion}>
      <DimLine axis="x" a={0} b={length} at={[0, H - TOP_LINE_DROP_CM, z]} tickAxis="y" label={`طولِ دیوار: ${cm(length)}`} labelOffset={[0, 14, 0]} color={DIM_COLORS.wall} priority={3} />
      {split != null && split > level && split < H ? (
        <>
          <DimLine axis="y" a={level} b={split} at={[vx, 0, z]} tickAxis="x" label={`پوششِ پایین: ${cm(split - level)}`} labelOffset={[26, 0, 0]} color={DIM_COLORS.wall} priority={2} />
          <DimLine axis="y" a={split} b={H} at={[vx, 0, z]} tickAxis="x" label={`پوششِ بالا: ${cm(H - split)}`} labelOffset={[26, 0, 0]} color={DIM_COLORS.wall} priority={2} />
        </>
      ) : (
        <DimLine axis="y" a={level} b={H} at={[vx, 0, z]} tickAxis="x" label={`ارتفاعِ دیوار: ${cm(H - level)}`} labelOffset={[26, 0, 0]} color={DIM_COLORS.wall} priority={2} />
      )}
    </group>
  );
}

const ITEM_TITLES = { door: 'در', window: 'پنجره', opening: 'بازشو' };

// ۲) در/پنجره/بازشو: عرض، ارتفاع، (پنجره) ارتفاع از کف، و فاصله تا دو گوشه‌ی دیوار
function ItemDimensions({ wall, side, item, wallThickness, wallHeightCm, level = 0 }) {
  const { length, quaternion } = useWallFrame(wall);
  if (length <= 0 || !item) return null;
  const z = (side === 'a' ? 1 : -1) * (wallThickness / 2 + FACE_GAP_CM);
  const H = wallHeightCm;
  const title = ITEM_TITLES[item.kind] || '';
  // اختلافِ سطحِ دو طرفِ در/بازشو: سمتِ کاربر = side، سمتِ مقابل = دیگری
  const myLevel = item.levels ? item.levels[side] || 0 : 0;
  const otherLevel = item.levels ? item.levels[side === 'a' ? 'b' : 'a'] || 0 : 0;
  const stepCm = Math.abs(otherLevel - myLevel);
  const stepLow = Math.min(myLevel, otherLevel) + 0.5;
  const stepUp = otherLevel > myLevel;
  const width = item.end - item.start;
  const height = item.top - item.bottom;
  // خطِ عرض کمی بالاتر از لبه‌ی بالای در/پنجره (یا اگر تا سقف است، کمی پایین‌تر)
  const widthY = item.top + ITEM_SIDE_GAP_CM < H - 8 ? item.top + ITEM_SIDE_GAP_CM : item.top - ITEM_SIDE_GAP_CM;
  const rightX = Math.min(length - 4, item.end + ITEM_SIDE_GAP_CM);
  const leftX = Math.max(4, item.start - ITEM_SIDE_GAP_CM);

  return (
    <group position={[wall.x1, 0, wall.y1]} quaternion={quaternion}>
      <DimLine axis="x" a={0} b={length} at={[0, H - TOP_LINE_DROP_CM, z]} tickAxis="y" label={`طولِ دیوار: ${cm(length)}`} labelOffset={[0, 14, 0]} color={DIM_COLORS.wall} priority={3} />
      <DimLine axis="x" a={item.start} b={item.end} at={[0, widthY, z]} tickAxis="y" label={`عرضِ ${title}: ${cm(width)}`} labelOffset={[0, 14, 0]} color={DIM_COLORS.item} priority={1} />
      <DimLine axis="y" a={item.bottom} b={item.top} at={[rightX, 0, z]} tickAxis="x" label={`ارتفاعِ ${title}: ${cm(height)}`} labelOffset={[34, 0, 0]} color={DIM_COLORS.item} priority={1} />
      {/* پله‌ی آستانه (اختلافِ سطحِ دو اتاقِ دو طرف) — خطِ عمودی درست لبِ بازشو، از کفِ پایین‌تر تا بالاتر */}
      {stepCm >= 1 && (
        <DimLine
          axis="y"
          a={stepLow}
          b={stepLow + stepCm}
          at={[item.start + 10, 0, z]}
          tickAxis="x"
          label={`پله: ${cm(stepCm)} ${stepUp ? '↑ بالا' : '↓ پایین'}`}
          labelOffset={[-36, 18, 0]}
          color={DIM_COLORS.step}
          priority={0}
        />
      )}
      {item.kind === 'window' && item.bottom - level > 1 && (
        <DimLine axis="y" a={level} b={item.bottom} at={[leftX, 0, z]} tickAxis="x" label={`از کف: ${cm(item.bottom - level)}`} labelOffset={[-30, 0, 0]} color={DIM_COLORS.offset} priority={2} />
      )}
      {item.start > 1 && (
        <DimLine axis="x" a={0} b={item.start} at={[0, level + CORNER_LINE_Y_CM, z]} tickAxis="y" label={`تا گوشه: ${cm(item.start)}`} labelOffset={[0, 14, 0]} color={DIM_COLORS.offset} priority={2} />
      )}
      {length - item.end > 1 && (
        <DimLine axis="x" a={item.end} b={length} at={[0, level + CORNER_LINE_Y_CM, z]} tickAxis="y" label={`تا گوشه: ${cm(length - item.end)}`} labelOffset={[0, 14, 0]} color={DIM_COLORS.offset} priority={2} />
      )}
    </group>
  );
}

// ۳) کف: طول و عرضِ اتاق + مساحت
function RoomDimensions({ room, level = 0 }) {
  const x1 = Math.min(room.x1, room.x2);
  const x2 = Math.max(room.x1, room.x2);
  const z1 = Math.min(room.y1, room.y2);
  const z2 = Math.max(room.y1, room.y2);
  const w = x2 - x1;
  const d = z2 - z1;
  const insetX = Math.min(FLOOR_EDGE_INSET_CM, w / 4);
  const insetZ = Math.min(FLOOR_EDGE_INSET_CM, d / 4);
  const areaM2 = (w * d) / 10000;
  return (
    <group>
      <DimLine axis="x" a={x1} b={x2} at={[0, level + FLOOR_LINE_Y_CM, z1 + insetZ]} tickAxis="z" label={`طولِ اتاق: ${cm(w)}`} labelOffset={[0, 0, 18]} color={DIM_COLORS.room} priority={2} />
      <DimLine axis="z" a={z1} b={z2} at={[x1 + insetX, level + FLOOR_LINE_Y_CM, 0]} tickAxis="x" label={`عرضِ اتاق: ${cm(d)}`} labelOffset={[30, 0, 0]} color={DIM_COLORS.room} priority={2} />
      <Label position={[(x1 + x2) / 2, level + FLOOR_LINE_Y_CM, (z1 + z2) / 2]} strong color={DIM_COLORS.room} priority={0}>
        {room.label ? `${room.label} · ` : ''}مساحت: {faNumber(areaM2, 1)} مترمربع
        {level ? ` · کف ${faNumber(Math.abs(level))} سانت ${level > 0 ? 'بالاتر' : 'پایین‌تر'}` : ''}
      </Label>
    </group>
  );
}

// داخلِ <group scale={[M,M,M]}>ِ صحنه قرار می‌گیرد (واحد = سانتی‌متر). posRef/yawRef/pitchRef همان‌هایی است
// که FirstPersonRig برای جای کاربر و جهتِ نگاه به‌روز می‌کند. wallItems: Map(wallId → [{kind,id,start,end,
// bottom,top}]) — در/پنجره/بازشوهای هر دیوار، به سانتی‌متر در امتدادِ همان دیوار (Create3D می‌سازد)
export default function EngineeringGlasses({
  active,
  walls,
  rooms,
  wallItems,
  posRef,
  yawRef,
  pitchRef,
  wallThickness,
  wallHeightCm,
  levelAt,
}) {
  const [target, setTarget] = useState(null);
  const keyRef = useRef(null);
  const labels = useLabelRegistry();

  useFrame(() => {
    const next =
      active && posRef.current
        ? computeGlassesTarget({
            walls,
            rooms,
            wallItems,
            pos: posRef.current,
            yaw: yawRef.current || 0,
            pitch: pitchRef?.current || 0,
            level: levelAt ? levelAt(posRef.current.x, posRef.current.z) : 0,
          })
        : null;
    const key = next ? next.key : null;
    if (key !== keyRef.current) {
      keyRef.current = key;
      setTarget(next);
    }
  });

  if (!active || !target) return null;

  let content = null;
  if (target.kind === 'floor') {
    const room = (rooms || []).find((r) => r.id === target.roomId);
    content = room ? <RoomDimensions room={room} level={target.level} /> : null;
  } else {
    const wall = walls.find((w) => w.id === target.wallId);
    if (!wall) return null;
    if (target.kind === 'item') {
      const item = wallItems?.get(wall.id)?.find((it) => `${it.kind}:${it.id}` === target.itemKey);
      content = (
        <ItemDimensions wall={wall} side={target.side} item={item} wallThickness={wallThickness} wallHeightCm={wallHeightCm} level={target.level} />
      );
    } else {
      content = (
        <WallDimensions
          wall={wall}
          side={target.side}
          along={target.along}
          wallThickness={wallThickness}
          wallHeightCm={wallHeightCm}
          level={target.level}
        />
      );
    }
  }

  return (
    <LabelLayoutContext.Provider value={labels.register}>
      <LabelLayout listRef={labels.listRef} />
      {content}
    </LabelLayoutContext.Provider>
  );
}
